import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import PasswordStrength, { isPasswordAcceptable } from "@/components/PasswordStrength";
import { toast } from "sonner";

const PW_MIN = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwScore, setPwScore] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < PW_MIN) { toast.error(`Password must be at least ${PW_MIN} characters.`); return; }
    if (!isPasswordAcceptable(password, pwScore)) { toast.error("Password is too easy to guess. Add unusual words, mix in numbers/symbols, or make it longer."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated. Log in with your new password.");
      navigate("/auth", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
        <div className="w-full max-w-md panel-glass p-6 sm:p-8 md:p-10 relative corner-marks" data-testid="reset-password-card">
          <div className="terminal-label mb-2 text-primary">[ ACCOUNT.RESET ]</div>
          <h1 className="font-display text-3xl uppercase font-bold mb-2 tracking-tight">New password</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Choose a new password to log back in.
          </p>
          {!token && (
            <p className="text-sm text-primary mb-4">No reset token in URL - please use the link from your email.</p>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="terminal-label">New password</label>
              <input
                data-testid="reset-password"
                type="password"
                required
                className="input-base mt-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <PasswordStrength password={password} onScoreChange={setPwScore} />
            </div>
            <div>
              <label className="terminal-label">Confirm new password</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                required
                className="input-base mt-2"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <button data-testid="reset-submit" type="submit" disabled={busy || !token || !isPasswordAcceptable(password, pwScore)} className="btn-primary w-full mt-6">
              {busy ? "..." : "Update password →"}
            </button>
          </form>
        </div>
      </div>
      <SiteFooter variant="site" />
    </div>
  );
}
